// tiktok-post.ts — (tf3-26; planning 10-15 00:15, PUBLISHING-TIKTOK-REVIEW §00:15) the TikTok post form's rules, kept
// pure so they are tested on their own. TikTok's Content Sharing Guidelines, as the form must follow them:
//   · the creator's nickname, from a fresh creator_info; a creator who can't post now is blocked, with the reason;
//   · the video's length checked against max_video_post_duration_sec;
//   · an editable title; privacy chosen from the creator's options only, with NO default;
//   · Allow Comment / Duet / Stitch OFF by default, greyed when the creator has them off;
//   · the commercial content disclosure OFF by default; when on, "Your brand" and/or "Branded content" (one required);
//     branded content can't be private;
//   · the consent line above the button; nothing sent to TikTok before the click.
import { w, PAGE_WORDS, type PageWordKey } from "@/lib/words.ts";   // (B107)

/** GET /api/publishing/channels/:channelId/creator-info (the package's route; always fresh). */
export interface CreatorInfo {
  nickname: string; avatarUrl: string | null; privacyLevelOptions: string[];
  commentDisabled: boolean; duetDisabled: boolean; stitchDisabled: boolean;
  maxVideoPostDurationSec: number; canPost: boolean; cannotPostReason: string | null;
}
/** What the creator chose on the form (allow = the toggle's state; the API's disable_* is its opposite). */
export interface TikTokFormState {
  title: string; privacyLevel: string; allowComment: boolean; allowDuet: boolean; allowStitch: boolean;
  disclose: boolean; yourBrand: boolean; brandedContent: boolean;
}
/** The choices the backend and the adapter take (the contract's names). */
export interface TikTokChoices {
  title: string; privacyLevel: string; disableComment: boolean; disableDuet: boolean; disableStitch: boolean;
  brandContentToggle: boolean; brandOrganicToggle: boolean; consentAt: string;
}

export const TITLE_MAX = 2200;
/** Everything off, privacy unset: the guidelines' defaults. */
export const initialFormState = (title: string): TikTokFormState => ({
  title: title.slice(0, TITLE_MAX), privacyLevel: "", allowComment: false, allowDuet: false, allowStitch: false,
  disclose: false, yourBrand: false, brandedContent: false,
});

/** (tf3-27) the TikTok version's fitted caption as the form's starting title (at most TITLE_MAX; the creator may edit it). */
export const prefillTitle = (t: string): string => t.slice(0, TITLE_MAX);

// (B107) every customer line here is read through w() when shown (Admin → Words; the guideline lines locked). The constants
//  still exported below are those lines' defaults, which the tests compare against.
const PRIVACY_KEY: Record<string, PageWordKey> = {
  PUBLIC_TO_EVERYONE: "page.tiktok.privacy.everyone", MUTUAL_FOLLOW_FRIENDS: "page.tiktok.privacy.friends",
  FOLLOWER_OF_CREATOR: "page.tiktok.privacy.followers", SELF_ONLY: "page.tiktok.privacy.only_me",
};
export const privacyLabel = (o: string) => (PRIVACY_KEY[o] ? w(PRIVACY_KEY[o]!) : o);
/** Branded content can't be private: "Only me" is offered, disabled, with why. */
export const privacyOptionDisabled = (o: string, s: TikTokFormState) => s.disclose && s.brandedContent && o === "SELF_ONLY";

export const MUSIC_USAGE_URL = "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
export const BRANDED_CONTENT_POLICY_URL = "https://www.tiktok.com/legal/page/global/bc-policy/en";
/** The consent line's parts (the page renders the links). */
export const consentLine = (s: TikTokFormState) => (s.disclose && s.brandedContent
  ? { text: w("page.tiktok.consent"), links: [[w("page.tiktok.link.bc_policy"), BRANDED_CONTENT_POLICY_URL], [w("page.tiktok.link.music"), MUSIC_USAGE_URL]] as const }
  : { text: w("page.tiktok.consent"), links: [[w("page.tiktok.link.music"), MUSIC_USAGE_URL]] as const });
/** How TikTok will label it, as the guidelines ask the form to say. */
export const disclosureLabel = (s: TikTokFormState): string | null =>
  !s.disclose ? null
    : s.brandedContent ? w("page.tiktok.labeled_paid")
      : s.yourBrand ? w("page.tiktok.labeled_promo") : null;

/** (audit 10-15) the preview's length: a number, null while it loads, or UNREADABLE (no file, a load error, NaN or
 *  Infinity) — the length can't be checked, so the post is held, named, never left "checking" for ever. */
export const UNREADABLE = "unreadable" as const;
export type VideoLength = number | null | typeof UNREADABLE;
export const lengthOf = (d: number): VideoLength => (Number.isFinite(d) && d > 0 ? d : UNREADABLE);
export const LENGTH_UNREADABLE = PAGE_WORDS["page.tiktok.length_unread"];
export const LIMIT_UNREADABLE = PAGE_WORDS["page.tiktok.limit_unread"];

/** Why the Post button is off — every reason, in the order the form shows them; none → it can post. `durationSec` is the
 *  preview's own length (null until it has loaded). `held`: (audit 10-15) why the video can't be posted now at all
 *  (a newer cut being finished), from the page. */
export function whyNotYet(info: CreatorInfo | null, s: TikTokFormState, durationSec: VideoLength, held: string | null = null): string[] {
  if (!info) return [w("page.tiktok.reading_settings")];
  if (!info.canPost) return [info.cannotPostReason || w("page.tiktok.cant_post")];
  const out: string[] = [];
  if (held) out.push(held);
  const max = info.maxVideoPostDurationSec;
  if (typeof max !== "number" || !Number.isFinite(max) || max <= 0) out.push(w("page.tiktok.limit_unread"));
  else if (durationSec === UNREADABLE) out.push(w("page.tiktok.length_unread"));
  else if (durationSec === null) out.push(w("page.tiktok.checking_length"));
  else if (durationSec > info.maxVideoPostDurationSec)
    out.push(w("page.tiktok.too_long", { s: Math.ceil(durationSec), max: info.maxVideoPostDurationSec }));
  if (s.title.length > TITLE_MAX) out.push(w("page.tiktok.title_max", { max: TITLE_MAX }));
  if (!s.privacyLevel) out.push(w("page.tiktok.choose_privacy"));
  else if (!info.privacyLevelOptions.includes(s.privacyLevel)) out.push(w("page.tiktok.privacy_unavailable"));
  if (s.disclose && !s.yourBrand && !s.brandedContent) out.push(w("page.tiktok.indicate_promotion"));
  if (privacyOptionDisabled(s.privacyLevel, s)) out.push(w("page.tiktok.branded_not_private"));
  return out;
}

/** The choices sent at the click (consentAt = that moment). A toggle the creator has off is sent as disabled. */
export function choicesOf(info: CreatorInfo, s: TikTokFormState, now: Date = new Date()): TikTokChoices {
  return {
    title: s.title, privacyLevel: s.privacyLevel,
    disableComment: info.commentDisabled || !s.allowComment,
    disableDuet: info.duetDisabled || !s.allowDuet,
    disableStitch: info.stitchDisabled || !s.allowStitch,
    brandContentToggle: s.disclose && s.brandedContent,
    brandOrganicToggle: s.disclose && s.yourBrand,
    consentAt: now.toISOString(),
  };
}

/** (audit 10-15) the workers' own words for a target's state, as the contract's (pending → queued, publishing → processing). */
const STATUS_ALIAS: Record<string, string> = { pending: "queued", publishing: "processing" };
/** A target's publish_status (the package's: queued | processing | retrying | published | failed), in words. */
export function statusText(t: { publishStatus: string; failureReason?: string | null }, privacyLevel: string): { text: string; done: boolean } {
  switch (STATUS_ALIAS[t.publishStatus] ?? t.publishStatus) {
    case "queued": return { text: w("page.tiktok.status.waiting"), done: false };
    case "processing": return { text: w("page.tiktok.status.processing"), done: false };
    // (tf3-28; C1.6b handoff §3, AL-C-6) the post is being sent again after a failed try: not done, not failed
    case "retrying": return { text: w("page.tiktok.status.resending"), done: false };
    case "published": return { text: privacyLevel === "SELF_ONLY" ? w("page.tiktok.status.posted_private") : w("page.tiktok.status.posted"), done: true };
    case "failed": return { text: w("page.tiktok.status.failed", { reason: t.failureReason || w("page.post.no_reason") }), done: true };
    default: return { text: w("page.tiktok.status.other", { status: t.publishStatus }), done: false };
  }
}

/** (audit 10-15) after the click, the status is asked every 5 s for at most 10 minutes. */
export const POLL_MS = 5000;
export const POLL_MAX = 120;
export const STILL_PROCESSING = PAGE_WORDS["page.tiktok.status.slow"];
export const NO_TARGET = PAGE_WORDS["page.tiktok.status.no_target"];
/** What the form shows after the click, and whether to ask again: the target's status; the read's error; no target; the cap. */
export function afterPost(read: { targets: { channelId: string; publishStatus: string; failureReason?: string | null }[] } | undefined,
    readError: string | null, channelId: string, polls: number, privacyLevel: string): { text: string; again: boolean } {
  if (readError) return { text: w("page.tiktok.status.unreadable", { reason: readError }), again: false };
  if (!read) return { text: w("page.tiktok.status.waiting"), again: true };
  const t = read.targets.find((x) => x.channelId === channelId);
  if (!t) return { text: w("page.tiktok.status.no_target"), again: false };
  const st = statusText(t, privacyLevel);
  if (st.done) return { text: st.text, again: false };
  if (polls >= POLL_MAX) return { text: w("page.tiktok.status.slow"), again: false };
  return { text: st.text, again: true };
}
